/**
 * Submission layer for all front-end forms.
 *
 * Forms call `submitLead` with the collected values. This module POSTs to the
 * `/api/leads` route handler, which validates, drops honeypot submissions,
 * and forwards the lead to the dbm-crm-backend FastAPI service.
 *
 * The CRM backend persists the lead and sends Brevo notifications — the
 * marketing site never talks to Postgres or Brevo directly.
 *
 * Required env (see `.env.example`):
 *   DBM_API_URL       — FastAPI base URL (e.g. http://localhost:8000)
 *   LEAD_INGEST_KEY   — X-API-Key shared with the CRM backend
 */

import { analyticsEnabled } from "@/lib/analytics-preferences";

export type LeadValues = Record<string, string>;

/**
 * HubSpot's visitor cookie, set by the tracking script. The CRM backend passes
 * it to HubSpot as `context.hutk` so the contact inherits the tracked sessions
 * (Paid Search, Organic, Referral…). Without it every contact is Direct Traffic.
 * Only sent when the visitor has website tracking enabled.
 */
export function hubspotUtk(): string | undefined {
  if (typeof document === "undefined" || !analyticsEnabled()) return undefined;
  return document.cookie.match(/(?:^|;\s*)hubspotutk=([0-9a-f]{32})(?:;|$)/i)?.[1];
}

export async function submitLead(values: LeadValues, source = "lead"): Promise<{ ok: true; reference: string }> {
  const hutk = hubspotUtk();
  const res = await fetch("/api/leads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...values, source, ...(hutk ? { hutk } : {}) }),
  });

  if (!res.ok) {
    const detail = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(detail?.error ?? "Submit failed");
  }

  const data = (await res.json()) as { ok: boolean; reference?: string };
  if (!data.ok) throw new Error("Submit failed");
  return { ok: true, reference: data.reference ?? `DBM-${Date.now().toString(36).toUpperCase()}` };
}
